import { matchExitsToBrackets, findMatchingTemplate, findVersionForTrade } from '../services/ntTemplateParser';
import { futuresSpecs, getSpec } from '../config/futuresSpecs';
import { safePnl } from '../utils/formatters';
import { calcTradeStats, calcRStats } from '../services/statsCalculator';

/**
 * Custom hook that provides all ATM-related analytics functions.
 * Extracted from TradingJournal.js for modularization (Phase 1B).
 *
 * Pure analytics only — CRUD functions (updateAtmDescription, addAtmRule, etc.)
 * and NT template loading stay in TradingJournal.js until Phase 4.
 */
const useAtmAnalytics = ({ setupAtmBaseTrades, closedFilteredTrades, ntTemplates, atmDetails }) => {

  // Get all ATM strategies from trades combined with custom details
  const getAllAtmStrategiesWithDetails = () => {
    // Get unique ATM strategies from trades
    const atmFromTrades = [...new Set(closedFilteredTrades
      .filter(t => t.atmStrategy)
      .map(t => t.atmStrategy)
    )];

    // Get ATM strategies that have details but might not have trades
    const atmWithDetails = Object.keys(atmDetails);

    // Combine both lists
    const allAtmStrategies = [...new Set([...atmFromTrades, ...atmWithDetails])];

    return allAtmStrategies.map(atm => ({
      id: atm.replace(/\s+/g, '-').toLowerCase(),
      name: atm,
      description: atmDetails[atm]?.description || '',
      rules: atmDetails[atm]?.rules || [],
      hasTrades: atmFromTrades.includes(atm)
    }));
  };

  // Get ATM stats for library display
  const getAtmStats = (atmName) => {
    const atmTrades = setupAtmBaseTrades.filter(t => t.atmStrategy === atmName);
    if (atmTrades.length === 0) return null;

    const stats = calcTradeStats(atmTrades);
    const rStats = calcRStats(atmTrades);

    return {
      trades: stats.totalTrades,
      wins: stats.wins,
      losses: stats.losses,
      winRate: stats.winRate.toFixed(1),
      totalPnL: stats.totalPnL,
      avgPnL: stats.avgPnL,
      profitFactor: stats.profitFactor,
      avgR: rStats.avgR
    };
  };

  const getAtmBracketAnalysis = (atmName) => {
    const template = findMatchingTemplate(atmName, ntTemplates.atm);
    if (!template) return { parsedAtm: null, bracketResults: [], tpVsTrail: null };

    const atmTrades = setupAtmBaseTrades.filter(t => t.atmStrategy === atmName);
    if (atmTrades.length === 0) return { parsedAtm: template, bracketResults: [], tpVsTrail: null };

    // getSpec() handles all NT symbol forms internally — "MNQ", "MNQ 06-26",
    // "MNQM6" all resolve. The old manual sanitization was a duplicate of
    // what baseSymbol() does now.
    const spec = getSpec(atmTrades[0]?.symbol) || { tickSize: 0.25, tickValue: 5.0, pointValue: 20 };

    const bracketAccum = {};
    template.brackets.forEach(b => {
      bracketAccum[b.index] = { bracketIndex: b.index, exitType: b.exitType, quantity: b.quantity, trades: 0, totalR: 0, rCount: 0, wins: 0, stopStrategy: b.stopStrategy };
    });

    const tpStats = { trades: 0, totalR: 0, rCount: 0, wins: 0 };
    const trailStats = { trades: 0, totalR: 0, rCount: 0, wins: 0 };

    atmTrades.forEach(trade => {
      const matched = matchExitsToBrackets(trade, template, spec);
      if (!matched) return;

      matched.forEach(m => {
        if (bracketAccum[m.bracketIndex]) {
          const ba = bracketAccum[m.bracketIndex];
          ba.trades++;
          ba.totalR += m.rCaptured;
          ba.rCount++;
          if (m.rCaptured > 0) ba.wins++;
        }

        const bucket = m.exitType === 'takeProfit' ? tpStats : trailStats;
        bucket.trades++;
        bucket.totalR += m.rCaptured;
        bucket.rCount++;
        if (m.rCaptured > 0) bucket.wins++;
      });
    });

    const bracketResults = Object.values(bracketAccum).map(b => ({
      ...b,
      avgR: b.rCount > 0 ? b.totalR / b.rCount : 0,
      winRate: b.trades > 0 ? (b.wins / b.trades) * 100 : 0
    }));

    const formatBucket = (b) => b.trades === 0 ? null : ({
      trades: b.trades,
      avgR: b.rCount > 0 ? b.totalR / b.rCount : 0,
      totalR: b.totalR,
      winRate: b.trades > 0 ? (b.wins / b.trades) * 100 : 0
    });

    const tpVsTrail = (tpStats.trades > 0 || trailStats.trades > 0) ? {
      takeProfit: formatBucket(tpStats),
      trailingStop: formatBucket(trailStats)
    } : null;

    return { parsedAtm: template, bracketResults, tpVsTrail };
  };

  const getTrailParameterCorrelation = (atmName) => {
    const template = findMatchingTemplate(atmName, ntTemplates.atm);
    if (!template) return [];

    const atmTrades = setupAtmBaseTrades.filter(t => t.atmStrategy === atmName);
    if (atmTrades.length === 0) return [];
    // getSpec() handles all NT symbol forms internally — "MNQ", "MNQ 06-26",
    // "MNQM6" all resolve. The old manual sanitization was a duplicate of
    // what baseSymbol() does now.
    const spec = getSpec(atmTrades[0]?.symbol) || { tickSize: 0.25, tickValue: 5.0, pointValue: 20 };

    const paramGroups = {};

    atmTrades.forEach(trade => {
      const matched = matchExitsToBrackets(trade, template, spec);
      if (!matched) return;

      matched.forEach(m => {
        if (m.exitType !== 'trailingStop' || !m.stopStrategy) return;
        m.stopStrategy.autoTrailSteps.forEach((step, i) => {
          const key = `PT${step.profitTrigger}_F${step.frequency}_SL${step.stopLoss}_S${i + 1}`;
          if (!paramGroups[key]) {
            paramGroups[key] = { ...step, stage: i + 1, paramKey: key, trades: 0, totalR: 0, wins: 0 };
          }
          paramGroups[key].trades++;
          paramGroups[key].totalR += m.rCaptured;
          if (m.rCaptured > 0) paramGroups[key].wins++;
        });
      });
    });

    return Object.values(paramGroups)
      .map(g => ({
        ...g,
        avgR: g.trades > 0 ? g.totalR / g.trades : 0,
        winRate: g.trades > 0 ? (g.wins / g.trades) * 100 : 0
      }))
      .sort((a, b) => b.avgR - a.avgR);
  };

  const getVersionPerformance = (atmName, version, allVersions) => {
    const atmTrades = setupAtmBaseTrades.filter(t => t.atmStrategy === atmName);
    if (atmTrades.length === 0) return null;

    const versionTrades = atmTrades.filter(t => {
      const matched = findVersionForTrade(t, allVersions);
      return matched && matched.versionId === version.versionId;
    });

    if (versionTrades.length === 0) return { trades: 0, wins: 0, winRate: 0, avgR: null, totalPnL: 0, edgeScore: 0 };

    const stats = calcTradeStats(versionTrades);
    const rStats = calcRStats(versionTrades);
    const avgR = rStats.tradesWithR > 0 ? rStats.avgR : null;

    // Edge score needs winner/loser R separately
    const winners = versionTrades.filter(t => safePnl(t) > 0 && t.rMultiple && !isNaN(parseFloat(t.rMultiple)));
    const losers = versionTrades.filter(t => safePnl(t) <= 0 && t.rMultiple && !isNaN(parseFloat(t.rMultiple)));
    const avgWinR = winners.length > 0 ? winners.reduce((s, t) => s + Math.abs(parseFloat(t.rMultiple)), 0) / winners.length : 0;
    const avgLossR = losers.length > 0 ? losers.reduce((s, t) => s + Math.abs(parseFloat(t.rMultiple)), 0) / losers.length : 0;
    const edgeScore = (stats.winRate / 100 * avgWinR) - ((1 - stats.winRate / 100) * avgLossR);

    return { trades: stats.totalTrades, wins: stats.wins, winRate: stats.winRate, avgR, totalPnL: stats.totalPnL, edgeScore, avgWinR, avgLossR };
  };

  // Cross-ATM Comparison: group ATMs sharing the same bracket structure
  const getCrossAtmGroups = () => {
    // Get all ATM names from trades
    const atmNames = [...new Set(setupAtmBaseTrades.filter(t => t.atmStrategy).map(t => t.atmStrategy))];
    if (atmNames.length < 2) return [];

    // Group by bracket fingerprint (structural similarity)
    const groups = {};

    atmNames.forEach(atmName => {
      const template = findMatchingTemplate(atmName, ntTemplates.atm);
      const atmTrades = setupAtmBaseTrades.filter(t => t.atmStrategy === atmName);
      if (atmTrades.length === 0) return;

      // Build fingerprint — use parsed template if available, else derive from trade data
      let groupKey;
      if (template) {
        // Structural fingerprint from XML: brackets count, quantities, SL, exit types
        const structKey = template.brackets.map(b =>
          `${b.quantity}C_${b.stopLossTicks}SL_${b.exitType === 'takeProfit' ? b.targetTicks + 'TP' : 'TRAIL'}`
        ).join('|');
        groupKey = `${template.entryQuantity}Q_${structKey}`;
      } else {
        // No template loaded — group by entry quantity from trade data
        const qty = atmTrades[0]?.quantity || 'unknown';
        groupKey = `noTemplate_${qty}`;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = {
          fingerprint: groupKey,
          template, // store one template as reference (all share same structure)
          members: []
        };
      }

      // Compute stats for this ATM
      const atmStats = calcTradeStats(atmTrades);
      const atmRStats = calcRStats(atmTrades);
      const { wins, losses, totalPnL, avgPnL, winRate, profitFactor } = atmStats;
      const avgR = atmRStats.avgR;

      // Get bracket-level analysis
      const bracketAnalysis = getAtmBracketAnalysis(atmName);

      // Try to extract timeframe hint from ATM name
      const _nameLower = atmName.toLowerCase();
      let timeframeHint = '';
      if (/\b2\s*m(in)?\b/i.test(atmName) || /\b2M\b/.test(atmName)) timeframeHint = '2M';
      else if (/\b5\s*m(in)?\b/i.test(atmName) || /\b5M\b/.test(atmName)) timeframeHint = '5M';
      else if (/\b15\s*m(in)?\b/i.test(atmName) || /\b15M\b/.test(atmName)) timeframeHint = '15M';
      else if (/\b1\s*m(in)?\b/i.test(atmName) || /\b1M\b/.test(atmName)) timeframeHint = '1M';
      else if (/\b3\s*m(in)?\b/i.test(atmName) || /\b3M\b/.test(atmName)) timeframeHint = '3M';
      else if (/\b10\s*m(in)?\b/i.test(atmName) || /\b10M\b/.test(atmName)) timeframeHint = '10M';
      else if (/\b30\s*m(in)?\b/i.test(atmName) || /\b30M\b/.test(atmName)) timeframeHint = '30M';
      else if (/\b1\s*h(r|our)?\b/i.test(atmName) || /\b1H\b/.test(atmName)) timeframeHint = '1H';

      groups[groupKey].members.push({
        name: atmName,
        timeframeHint,
        trades: atmTrades.length,
        wins, losses, winRate, totalPnL, avgPnL, profitFactor, avgR,
        bracketAnalysis
      });
    });

    // Only return groups with 2+ members (that's the whole point of cross-ATM)
    return Object.values(groups)
      .filter(g => g.members.length >= 2)
      .map(g => ({
        ...g,
        members: g.members.sort((a, b) => b.totalPnL - a.totalPnL) // best P&L first
      }))
      .sort((a, b) => b.members.length - a.members.length); // largest groups first
  };

  return {
    getAllAtmStrategiesWithDetails,
    getAtmStats,
    getAtmBracketAnalysis,
    getTrailParameterCorrelation,
    getVersionPerformance,
    getCrossAtmGroups
  };
};

export default useAtmAnalytics;
